/**
 * What goes on disk, and — more importantly — what does not.
 *
 * This is an allow-list, not a deny-list, for the same reason the service
 * worker's `/api/v1` rule is (see `vite.config.ts`): a read endpoint added
 * upstream must not become persisted mail by accident, before anyone has
 * decided whether keeping a copy of it on the user's disk is right.
 *
 * ## Persisted
 *
 * Exactly the four things it takes to *read* mail with no connection:
 *
 * - `['accounts']` — which mailboxes exist, so the switcher is not empty.
 * - `['account', id, 'folders']` — the folder tree.
 * - `['account', id, 'messages', filters]` — message lists (trimmed, below).
 * - `['account', id, 'message', messageId, …]` — bodies already opened.
 * - `['unified', 'messages', filters]` — the merged list (ADR 0056), which is
 *   the default landing view and would otherwise be the one empty screen.
 *
 * ## Deliberately not persisted
 *
 * - **`['session']` / `['session','list']`.** The server is the only authority
 *   on whether a session is alive; the service worker already refuses to cache
 *   `/api/v1/auth/*` for exactly this reason. A snapshot of *who* is signed in
 *   is kept separately in the meta record, is read back only while offline,
 *   and contains no credential — see `cacheDb.ts`.
 * - **Drafts, in every spelling.** `['account', id, 'drafts']` and the Drafts
 *   folder listing. A draft needs write-ahead semantics, not cache semantics
 *   (docs/plan/06-frontend-v1.md §1 and §3): re-showing a stale copy of
 *   something the user was in the middle of editing is worse than showing
 *   nothing. The mid-edit composer buffer never reaches the query cache at
 *   all, so this rule is belt and braces — and it stays, because that is a
 *   property of today's `useComposeDraft`, not a promise.
 * - **Grants and preferences** — remote-content rules, Sieve, notification
 *   preferences, push subscriptions. These decide what the app is *allowed* to
 *   do; a stale copy would outlive a revocation.
 * - **Raw RFC822 source, contacts, calendar events, the recipient directory,
 *   quota.** Large, or not part of reading mail. Bounded storage means saying
 *   no to things, and these are the things.
 */

/** How many pages of an infinite list survive to disk (plan §1). */
export const PERSISTED_PAGES = 2;

/** Folder ids whose *listing* is a draft listing and must not be persisted. */
const DRAFT_FOLDER_IDS = new Set(['drafts', 'draft']);

function isDraftListing(filters: unknown): boolean {
  if (typeof filters !== 'object' || filters === null) return false;
  const folderId = (filters as { folderId?: unknown }).folderId;
  return typeof folderId === 'string' && DRAFT_FOLDER_IDS.has(folderId.toLowerCase());
}

/**
 * True when this query key may be written to disk.
 *
 * Bare scope prefixes (`['account', id, 'messages']`) are rejected: they never
 * hold data of their own, and letting one through would persist an empty
 * record that a prefix patch could later be handed.
 */
export function isPersistableKey(key: readonly unknown[]): boolean {
  if (key.length === 0) return false;

  if (key[0] === 'accounts') return key.length === 1;

  if (key[0] === 'unified') {
    return key[1] === 'messages' && key.length === 3;
  }

  if (key[0] !== 'account' || typeof key[1] !== 'string') return false;

  switch (key[2]) {
    case 'folders':
      return key.length === 3;
    case 'messages':
      return key.length === 4 && !isDraftListing(key[3]);
    case 'message':
      // ['account', id, 'message', messageId] or […, messageId, 'block'|'load']
      return (key.length === 4 || key.length === 5) && typeof key[3] === 'string';
    default:
      return false;
  }
}

/**
 * The server's sanitizer version this build expects (`mail.SanitizerVersion`,
 * backend/internal/mail/sanitize.go; `whitelist.test.ts` reads the Go source
 * and fails if they differ). A body kept on disk is the SANITIZER'S OUTPUT,
 * and re-sanitizing old output cannot bring back what an older, stricter rule
 * removed: a body persisted before ADR 0081 has no stylesheet and never
 * would. So a persisted message body from any other version is dropped on
 * restore and refetched, the same lesson as `svgsanitize.Version` (#269).
 */
export const RENDER_VERSION = 4;

/** True for a persisted message detail whose HTML was made by another sanitizer version. */
export function isStaleRender(key: readonly unknown[], data: unknown): boolean {
  if (key[0] !== 'account' || key[2] !== 'message') return false;
  if (typeof data !== 'object' || data === null) return false;
  const detail = data as { bodyHtml?: unknown; renderVersion?: unknown };
  if (typeof detail.bodyHtml !== 'string' || detail.bodyHtml === '') return false;
  return detail.renderVersion !== RENDER_VERSION;
}

interface PagedData {
  pages: unknown[];
  pageParams: unknown[];
}

function isPaged(data: unknown): data is PagedData {
  return (
    typeof data === 'object' &&
    data !== null &&
    Array.isArray((data as PagedData).pages) &&
    Array.isArray((data as PagedData).pageParams)
  );
}

/**
 * Bound one record before it is written.
 *
 * An infinite list that the user scrolled for ten minutes is thirty pages of
 * mail; offline they need the top of the list, not their entire scroll
 * history. Keeping the first {@link PERSISTED_PAGES} also keeps the restored
 * list *resumable* — `fetchNextPage` picks up from the last kept cursor.
 */
export function trimForPersist(data: unknown): unknown {
  if (!isPaged(data)) return data;
  if (data.pages.length <= PERSISTED_PAGES) return data;
  return {
    ...data,
    pages: data.pages.slice(0, PERSISTED_PAGES),
    pageParams: data.pageParams.slice(0, PERSISTED_PAGES),
  };
}
